import { useLayoutEffect, useRef, type RefObject } from 'react';

import { OVERVIEW_BASE_FONT_PX, OVERVIEW_MIN_FONT_PX } from '../pages/blueprint-kitchen-overview-page.styles';
import { useOverviewFit } from './overview-fit-context';
import { usePageFontFactorOverride, useReportComputedFontFactor } from './page-font-factor-context';

/**
 * Shrinks the kitchen-overview body until it fits its fixed 850px sheet, so content the
 * line-count paginator under-estimates is never silently eaten by `overflow: hidden`.
 * The per-page font control sets the ceiling, and every overview sheet settles on the
 * smallest fit any of them needed so a split overview stays typographically uniform.
 * @param fitKey stable identity for this sheet within the overview set
 */
export const useOverviewShrinkToFit = (fitKey: string): RefObject<HTMLDivElement> => {
	const fontOverride = usePageFontFactorOverride();
	const reportComputedFactor = useReportComputedFontFactor();
	const { reportFit, appliedPx } = useOverviewFit();
	const bodyRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const el = bodyRef.current;
		if (!el || el.clientHeight === 0) return;

		// Always measure from the ceiling so the natural fit is independent of what is applied —
		// otherwise applying a shared smaller size would feed back into the next measurement.
		let naturalPx = OVERVIEW_BASE_FONT_PX * (fontOverride ?? 1);
		el.style.fontSize = `${naturalPx}px`;

		while (naturalPx > OVERVIEW_MIN_FONT_PX && el.scrollHeight > el.clientHeight) {
			naturalPx = Math.max(OVERVIEW_MIN_FONT_PX, naturalPx - 0.5);
			el.style.fontSize = `${naturalPx}px`;
		}

		reportFit(fitKey, naturalPx);

		const sizePx = appliedPx === null ? naturalPx : Math.min(appliedPx, naturalPx);
		el.style.fontSize = `${sizePx}px`;
		reportComputedFactor(Math.round((sizePx / OVERVIEW_BASE_FONT_PX) * 100) / 100);
	});

	return bodyRef;
};
